<script setup lang="ts" generic="T extends string | number |  undefined | null">
interface Props<T> {
  label: string
  required?: boolean,
  type?: 'text' | 'number' | 'tel' | 'email'
  validate?: (v: T) => string[]
}

const props = withDefaults(defineProps<Props<T>>(), {
  required: false,
  type: 'text',
  validate: () => []
});

const { t } = useEkI18n();

const userInput = defineModel<T>({
  set(value) {
    return value?.toString().trim();
  },
  required: true
});

const nonNullableUserInput = computed<string | number | undefined>({
  get() {
    return userInput.value === null ? undefined : userInput.value;
  },
  set(value) {
    userInput.value = value as T;
  }
});

const isValid = defineModel<boolean>('isValid');
const touched = ref<boolean>(false);

const isSet = computed(() => {
  if (userInput.value === undefined) {
    return false;
  }

  if (typeof userInput.value === 'string') {
    return userInput.value.length > 0;
  }

  if (typeof userInput.value === 'number') {
    return !isNaN(userInput.value);
  }

  return false;
});

const invalid = computed(() => errors.value.length > 0);
const inputLabel = computed(() => props.required ? props.label + '*' : props.label);
const statusClass = computed(() => touched.value && invalid.value ? 'ion-invalid ion-touched input-invalid' : '');
const errorText = computed<string | undefined>(() => errors.value.length > 0 ? errors.value[0] : undefined);
const errors = computed(() => {
  if (userInput.value === '' && props.required === false) {
    return [];
  }

  let errorMessages: string[] = [];

  const validationErrors = props.validate(userInput.value);

  if (validationErrors.length > 0) {
    errorMessages = errorMessages.concat(validationErrors);
  }

  if (props.required && isSet.value === false) {
    errorMessages.unshift(t('input.fieldRequired', { fieldName: props.label }));
  }

  return errorMessages;
});

watch(errors, (newErrors) => {
  isValid.value = newErrors.length === 0;
});

</script>

<template>
  <IonInput
    v-model="nonNullableUserInput"
    :label="inputLabel"
    label-placement="floating"
    :class="statusClass"
    class="input-valid"
    helper-text=" "
    :error-text="errorText"
    :type="type"
    @ion-blur="touched = true"
  />
</template>

<style scoped>
.input-valid {
  --border-color: var(--ion-color-step-200)
}

.input-invalid {
  --border-color: var(--ion-color-danger)
}
</style>
